import React from "react";
import { motion } from "framer-motion";
import Navigation from "./Navigation";
import Footer from "./Footer";
import { useLanguage } from "../context/LanguageContext";
import { experienceItems } from "../data/experienceData";

const Experience = () => {
  const { language } = useLanguage();

  const t = (value) => {
    if (typeof value === "string") return value;
    return value?.[language] || value?.en || "";
  };

  const translations = {
    title: {
      en: "Experience",
      zh: "经历",
      ja: "経験",
    },
    intro: {
      en: "Products and protocol surfaces I helped ship across the Sui ecosystem, shown as live links instead of resume bullets.",
      zh: "把我参与交付的 Sui 生态产品和协议模块直接挂到这里，用真实链接而不是简历式条目来展示。",
      ja: "履歴書の箇条書きではなく、実際に関わった Sui エコシステムのプロダクトとプロトコル面をライブリンク付きでまとめた。",
    },
    projects: {
      en: "Projects",
      zh: "项目",
      ja: "プロジェクト",
    },
  };

  return (
    <div className="bg-neutral-50 min-h-screen text-left">
      <Navigation activeSection="experience" />
      <div className="pt-24 pb-12 px-6">
        <div className="container mx-auto max-w-5xl">
          <header className="mb-10 max-w-4xl text-left">
            <h1 className="text-4xl md:text-5xl font-light mb-6 text-neutral-800">
              {t(translations.title)}
            </h1>
            <p className="text-xl text-neutral-600 leading-relaxed">
              {t(translations.intro)}
            </p>
          </header>

          <div className="border-t border-neutral-200">
            {experienceItems.map((item, index) => (
              <motion.article
                key={item.href}
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.45, delay: index * 0.08 }}
                className="border-b border-neutral-200 py-6 md:py-7"
              >
                <div className="flex items-start gap-3 md:gap-4">
                  <a
                    href={item.href}
                    target="_blank"
                    rel="noreferrer"
                    className="flex h-12 w-12 shrink-0 items-center justify-center"
                    aria-label={`${t(item.name)} logo`}
                  >
                    <img
                      src={item.logo}
                      alt={`${t(item.name)} logo`}
                      loading="lazy"
                      className="max-h-9 max-w-[3rem] w-auto object-contain"
                    />
                  </a>

                  <div className="min-w-0 flex-1 text-left">
                    <div className="min-w-0">
                      <a
                        href={item.href}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex max-w-full break-all text-xl md:text-2xl font-medium leading-tight text-neutral-900 underline-offset-4 transition-colors hover:text-neutral-600 hover:underline"
                      >
                        {t(item.name)}
                      </a>
                    </div>

                    <p className="mt-2 max-w-3xl text-sm md:text-base leading-relaxed text-neutral-700">
                      {t(item.brief)}
                    </p>

                    {item.projects?.length > 0 && (
                      <div className="mt-5">
                        <p className="mb-2 text-xs font-semibold uppercase tracking-[0.16em] text-neutral-500">
                          {t(translations.projects)}
                        </p>

                        <div className="border-t border-neutral-200">
                          {item.projects.map((project) => (
                            <div
                              key={project.href}
                              className="flex items-start gap-3 border-b border-neutral-200 py-3"
                            >
                              <a
                                href={project.href}
                                target="_blank"
                                rel="noreferrer"
                                className="flex h-10 w-10 shrink-0 items-center justify-center"
                                aria-label={`${t(project.name)} logo`}
                              >
                                <img
                                  src={project.logo}
                                  alt={`${t(project.name)} logo`}
                                  loading="lazy"
                                  className="max-h-8 max-w-[2.5rem] w-auto object-contain"
                                />
                              </a>

                              <div className="min-w-0 flex-1 text-left">
                                <div className="min-w-0">
                                  <a
                                    href={project.href}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="inline-flex max-w-full break-all text-base md:text-lg font-medium leading-tight text-neutral-900 underline-offset-4 transition-colors hover:text-neutral-600 hover:underline"
                                  >
                                    {t(project.name)}
                                  </a>
                                </div>

                                <p className="mt-1.5 max-w-3xl text-sm leading-relaxed text-neutral-700">
                                  {t(project.brief)}
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </motion.article>
            ))}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Experience;
